import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Square, Upload } from "lucide-react";
import {
  DeckUploadValidationError,
  getCardMetaBatch,
  streamUploadDeck,
  type Deck,
  type UploadDeckDoneEvent,
} from "../lib/api";
import type { AgentEvent } from "../lib/types";
import StreamingOutput from "../components/StreamingOutput";
import StreamingDeckResult from "../components/StreamingDeckResult";
import LiquidButton, { MANA_G } from "../components/ui/LiquidButton";
import MagicButton from "../components/ui/MagicButton";
import { useRefreshBalance } from "../lib/useBalance";
import { useMyUsername } from "../lib/useMyUsername";
import { ACTION_COSTS } from "../lib/credits";
import { parseArenaDeckText } from "../lib/arena-format";
import type { SectionKey } from "../lib/progressive-sections";

type FormError = { theme?: string; decklist?: string };

const UPLOAD_SECTION_ORDER: SectionKey[] = [
  "winCondition",
  "keyInteractions",
  "manaBase",
  "sideboardGuide",
  "weaknesses",
];

const PLACEHOLDER = `Deck
4 Monstrous Rage (MKM) 140
4 Slickshot Show-Off (OTJ) 146
4 Heartfire Hero (FDN) 127
...

Sideboard
2 Torch the Tower (OTJ) 155
...`;

export default function UploadDeck() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState("");
  const [brief, setBrief] = useState("");
  const [decklist, setDecklist] = useState("");
  const [running, setRunning] = useState(false);
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [lastText, setLastText] = useState("");
  const [submittedDeck, setSubmittedDeck] = useState<Deck | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const themeInputRef = useRef<HTMLInputElement | null>(null);
  const decklistInputRef = useRef<HTMLTextAreaElement | null>(null);
  const refreshBalance = useRefreshBalance();
  const myUsername = useMyUsername();
  const [fieldErrors, setFieldErrors] = useState<FormError>({});

  // Button stays clickable while inputs are empty so users get a clear
  // signal about what's missing instead of a silently-disabled button.
  // Real submit guard runs in onSubmit and surfaces inline errors.
  const canSubmit = !running;
  const cardNames = useMemo(() => {
    if (!submittedDeck) return [] as string[];
    const names = new Set<string>();
    for (const entry of submittedDeck.mainboard) names.add(entry.name);
    for (const entry of submittedDeck.sideboard) names.add(entry.name);
    return [...names];
  }, [submittedDeck]);
  const streamMetaQuery = useQuery({
    queryKey: ["card-meta-batch", cardNames],
    queryFn: () => getCardMetaBatch(cardNames),
    enabled: cardNames.length > 0,
  });

  const reset = (): void => {
    setEvents([]);
    setLastText("");
    setSubmittedDeck(null);
    setErrors([]);
    setError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const next: FormError = {};
    if (theme.trim().length === 0) {
      next.theme = "Give your deck a theme so it's easy to find later.";
    }
    if (decklist.trim().length === 0) {
      next.decklist = "Paste an Arena-format decklist to analyze.";
    }
    if (next.theme || next.decklist) {
      setFieldErrors(next);
      if (next.theme) themeInputRef.current?.focus();
      else if (next.decklist) decklistInputRef.current?.focus();
      return;
    }
    setFieldErrors({});
    const previewDeck = parseArenaDeckText(decklist.trim());
    reset();
    setSubmittedDeck(previewDeck);
    setRunning(true);
    const ctl = new AbortController();
    abortRef.current = ctl;
    try {
      await streamUploadDeck(
        {
          theme: theme.trim(),
          brief: brief.trim(),
          decklist: decklist.trim(),
        },
        (msg) => {
          if (msg.kind === "event") {
            setEvents((prev) => [...prev, msg.data]);
            if (msg.data.type === "assistant_text_delta") {
              const delta = msg.data.text;
              setLastText((prev) => prev + delta);
            } else if (msg.data.type === "turn_start") {
              setLastText("");
            } else if (msg.data.type === "assistant_text") {
              if (msg.data.text.length > 0) setLastText(msg.data.text);
            }
          } else if (msg.kind === "done") {
            refreshBalance();
            const done = msg.data as UploadDeckDoneEvent;
            if (done.saved_id && myUsername) {
              navigate(`/decks/${myUsername}/${done.saved_id}`);
            }
          } else if (msg.kind === "error") {
            refreshBalance();
            setError(msg.data.error);
          } else if (msg.kind === "save_error") {
            setError(`(persist) ${msg.data.error}`);
          }
        },
        ctl.signal,
      );
    } catch (err) {
      if (err instanceof DeckUploadValidationError) {
        setSubmittedDeck(null);
        setErrors(err.errors);
      } else if ((err as Error).name !== "AbortError") {
        setSubmittedDeck(null);
        setError((err as Error).message);
      }
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const onCancel = (): void => {
    abortRef.current?.abort();
  };

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-6">
      <header>
        <div className="eyebrow">Analyze</div>
        <h1 className="mt-2 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0]">
          Paste your list.
          <br />
          <span className="foil">Get a second opinion.</span>
        </h1>
        <p className="text-sm text-fg-muted mt-5 max-w-xl leading-relaxed">
          Paste an Arena-format decklist. The Oracle analyzes it — win
          condition, synergies, mana base, sideboard guide, weaknesses —
          and saves it so you can keep iterating in the chat.
        </p>
      </header>

      {error && (
        <div className="surface p-4 border-danger/40 bg-danger/10 text-sm text-danger">
          {error}
        </div>
      )}

      {errors.length > 0 && (
        <div className="surface p-4 border-danger/40 bg-danger/10 space-y-2">
          <div className="text-sm font-medium text-danger">
            Deck isn't Standard-legal:
          </div>
          <ul className="text-sm text-danger list-disc list-inside space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <form onSubmit={onSubmit} className="surface p-5 space-y-4">
        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-fg-muted uppercase tracking-wider">
            Theme <span className="text-danger">*</span>
          </span>
          <input
            ref={themeInputRef}
            type="text"
            className="input w-full"
            placeholder="e.g. Mono-red aggro"
            value={theme}
            onChange={(e) => {
              setTheme(e.target.value);
              if (fieldErrors.theme && e.target.value.trim().length > 0) {
                setFieldErrors((prev) => ({ ...prev, theme: undefined }));
              }
            }}
            disabled={running}
            maxLength={120}
            aria-invalid={!!fieldErrors.theme}
            aria-describedby={fieldErrors.theme ? "theme-error" : undefined}
          />
          {fieldErrors.theme && (
            <span
              id="theme-error"
              className="text-[11px] text-danger block"
            >
              {fieldErrors.theme}
            </span>
          )}
        </label>

        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-fg-muted uppercase tracking-wider">
            Notes (optional)
          </span>
          <textarea
            className="input w-full min-h-[64px] resize-y"
            placeholder="Anything the Oracle should know — matchups you're worried about, the plan for turn 3, questions."
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            disabled={running}
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-fg-muted uppercase tracking-wider">
            Decklist (Arena format) <span className="text-danger">*</span>
          </span>
          <textarea
            ref={decklistInputRef}
            className="input w-full min-h-[280px] font-mono text-[13px] leading-relaxed resize-y"
            placeholder={PLACEHOLDER}
            value={decklist}
            onChange={(e) => {
              setDecklist(e.target.value);
              if (fieldErrors.decklist && e.target.value.trim().length > 0) {
                setFieldErrors((prev) => ({ ...prev, decklist: undefined }));
              }
            }}
            disabled={running}
            spellCheck={false}
            aria-invalid={!!fieldErrors.decklist}
            aria-describedby={fieldErrors.decklist ? "decklist-error" : undefined}
          />
          {fieldErrors.decklist && (
            <span
              id="decklist-error"
              className="text-[11px] text-danger block"
            >
              {fieldErrors.decklist}
            </span>
          )}
          <span className="text-[11px] text-fg-subtle block">
            Count and card name per line. Set codes and collector numbers
            in parentheses are optional. Use a <code>Sideboard</code> header
            before sideboard cards.
          </span>
        </label>

        <div className="flex justify-center sm:justify-end gap-2">
          {running ? (
            <MagicButton
              variant="ghost"
              className="h-10 text-sm"
              onClick={onCancel}
            >
              <Square size={14} strokeWidth={2} />
              Stop
            </MagicButton>
          ) : (
            <LiquidButton
              className="h-10 text-sm"
              disabled={!canSubmit}
              onClick={() => {
                const form = document.querySelector("form");
                form?.requestSubmit();
              }}
              palette={MANA_G}
              trailing={
                <span className="text-[10px] font-mono opacity-70">
                  · {ACTION_COSTS.upload_deck}
                </span>
              }
            >
              <Upload size={14} strokeWidth={2} />
              Analyze &amp; save
            </LiquidButton>
          )}
        </div>
      </form>

      {submittedDeck && (events.length > 0 || running || lastText.length > 0) && (
        <StreamingDeckResult
          buffer={lastText}
          meta={streamMetaQuery.data}
          seedDeck={submittedDeck}
          sectionOrder={UPLOAD_SECTION_ORDER}
        />
      )}

      {(events.length > 0 || running) && (
        <StreamingOutput events={events} running={running} lastText={lastText} />
      )}
    </div>
  );
}
